import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { getImageUrl } from '../api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import ProductCard from '../components/ProductCard';
import { idFromSlug } from '../utils/slug';

const STAR = '\u2605';
const STAR_EMPTY = '\u2606';

const Stars = ({ rating, size = 16 }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <span style={{ color: '#f59e0b', fontSize: size, lineHeight: 1 }}>
      {STAR.repeat(full)}
      {half ? '\u00BD' : ''}
      {STAR_EMPTY.repeat(5 - full - (half ? 1 : 0))}
    </span>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const id = idFromSlug(slug);
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [isVIP, setIsVIP] = useState(false);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [selectedImg, setSelectedImg] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const { addItem } = useCart();

  // Admin inline edit
  const { isAdmin } = useAuth();
  const { toggle, isSaved } = useWishlist();
  const saved = isSaved(parseInt(id));
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);

  const openEdit = () => {
    setEditForm({
      name: product.name, description: product.description || '',
      price: product.price, stock_quantity: product.stock_quantity,
      brand: product.brand || '', category_id: product.category_id || '',
      is_active: product.is_active !== 0 ? 1 : 0,
    });
    if (categories.length === 0) api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(editForm).forEach(([k, v]) => fd.append(k, v));
      await api.put(`/products/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const r = await api.get(`/products/${id}`);
      setProduct(r.data);
      setEditing(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', body: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState(false);

  const loadReviews = () => {
    api.get(`/products/${id}/reviews`).then(r => {
      setReviews(r.data.reviews);
      setReviewStats(r.data.stats);
    }).catch(() => {});
  };

  useEffect(() => {
    setProduct(null); setRelated([]); setSelectedImg(0);
    if (user) api.get('/membership').then(r => { if (r.data?.status === 'active') setIsVIP(true); }).catch(() => {});
    api.get(`/products/${id}`).then(r => {
      setProduct(r.data);
      if (r.data.category_id) {
        api.get(`/products?category=${r.data.category_id}&limit=8&sort=newest`)
          .then(res => setRelated(res.data.products.filter(p => p.id !== r.data.id).slice(0, 4)))
          .catch(() => {});
      }
    }).catch(() => {});
    loadReviews();
    if (user) {
      api.get(`/products/${id}/reviews/can-review`)
        .then(r => { setCanReview(r.data.can_review); setAlreadyReviewed(r.data.already_reviewed); })
        .catch(() => {});
    }
  }, [id, user]);

  // Dynamic SEO: title + meta description + Open Graph per product
  useEffect(() => {
    if (!product) return;
    const siteName = 'StyleMe';
    const url = `https://styleme.club/product/${window.location.pathname.split('/product/')[1] || ''}`;
    const desc = product.description
      ? product.description.replace(/\s+/g, ' ').trim().substring(0, 155)
      : `Buy ${product.name} at StyleMe for $${product.price?.toFixed(2)}. Free shipping over $50. Easy 30-day returns.`;
    const image = product.images?.[0]?.image_url
      ? (product.images[0].image_url.startsWith('http') ? product.images[0].image_url : `https://styleme.club${product.images[0].image_url}`)
      : `https://styleme.club/logo-header.png`;

    document.title = `${product.name} � ${siteName}`;

    const setMeta = (sel, attr, val) => {
      let el = document.querySelector(sel);
      if (!el) { el = document.createElement('meta'); document.head.appendChild(el); }
      el.setAttribute(attr, val);
      return el;
    };

    const tags = [
      setMeta('meta[name="description"]',           'content', desc),
      setMeta('meta[name="keywords"]',              'content', `${product.name}, ${product.category_name || ''}, jewelry, fashion, StyleMe`),
      setMeta('meta[property="og:title"]',          'content', `${product.name} � ${siteName}`),
      setMeta('meta[property="og:description"]',    'content', desc),
      setMeta('meta[property="og:image"]',          'content', image),
      setMeta('meta[property="og:url"]',            'content', url),
      setMeta('meta[property="og:type"]',           'content', 'product'),
      setMeta('meta[property="og:site_name"]',      'content', siteName),
      setMeta('meta[name="twitter:card"]',          'content', 'summary_large_image'),
      setMeta('meta[name="twitter:title"]',         'content', `${product.name} � ${siteName}`),
      setMeta('meta[name="twitter:description"]',   'content', desc),
      setMeta('meta[name="twitter:image"]',         'content', image),
    ];

    // Set name/property attributes properly
    tags[2].setAttribute('property', 'og:title');
    tags[3].setAttribute('property', 'og:description');
    tags[4].setAttribute('property', 'og:image');
    tags[5].setAttribute('property', 'og:url');
    tags[6].setAttribute('property', 'og:type');
    tags[7].setAttribute('property', 'og:site_name');

    return () => {
      document.title = 'StyleMe - Shop | Style | Shine';
      tags.forEach(t => t.content = '');
    };
  }, [product]);

  if (!product) return (
    <div className="container" style={{ maxWidth: 1100, paddingTop: 40, paddingBottom: 40 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40 }}>
        <div className="skeleton" style={{ width: '100%', aspectRatio: '1', borderRadius: 16 }} />
        <div>
          <div className="skeleton" style={{ height: 16, width: '40%', marginBottom: 16 }} />
          <div className="skeleton" style={{ height: 32, width: '90%', marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 32, width: '60%', marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 28, width: '30%', marginBottom: 24 }} />
          <div className="skeleton" style={{ height: 48, width: '100%', marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 48, width: '100%', marginBottom: 24 }} />
          <div className="skeleton" style={{ height: 14, width: '80%', marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 14, width: '70%', marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 14, width: '60%' }} />
        </div>
      </div>
    </div>
  );

  // VIP gate — no longer a full lock screen, just grey out buy buttons below
  // (handled inline in the buy section)

  const handleAdd = () => {
    if (product.variants && product.variants.length > 0 && !selectedVariant) {
      alert('Please select a variant before adding to cart');
      return;
    }
    // Check stock vs what's already in cart
    const { items } = require('../context/CartContext') || {};
    const cartQty = 0; // addItem handles stock internally
    if (qty > product.stock_quantity) {
      alert(`Only ${product.stock_quantity} in stock`);
      return;
    }
    addItem(product, qty, selectedVariant);
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setReviewError('');
    try {
      await api.post(`/products/${id}/reviews`, reviewForm);
      setReviewSuccess(true); setCanReview(false);
      setReviewForm({ rating: 5, title: '', body: '' });
      loadReviews();
    } catch (err) {
      setReviewError(err.response?.data?.error || 'Failed to submit review');
    } finally { setSubmitting(false); }
  };

  const allImages = [];
  if (product.images && product.images.length > 0) {
    product.images.forEach(img => allImages.push(img.image_url));
  } else if (product.image_url) {
    allImages.push(product.image_url);
  }

  const currentImgUrl = allImages.length > 0 ? getImageUrl(allImages[selectedImg] || allImages[0]) : null;

  // When variant selected and has its own image, use it as the main image
  const variantImg = selectedVariant?.image_url ? getImageUrl(selectedVariant.image_url) : null;
  const displayImg = variantImg || currentImgUrl;

  // Computed values based on selected variant
  const effectivePrice = product.price + (selectedVariant?.price_adjustment || 0);
  const effectiveStock = selectedVariant ? selectedVariant.stock_quantity : product.stock_quantity;
  const inStock = effectiveStock > 0;

  // Viewers badge � seeded by product id + hour for realistic consistency
  const viewerCount = inStock ? Math.max(2, ((product.id * 7 + new Date().getHours() * 3) % 17) + 3) : 0;

  // Attribute keys covered by variants � hide these from static product info to avoid confusion
  const variantAttrKeys = product.variants && product.variants.length > 0
    ? [...new Set(product.variants.flatMap(v => Object.keys(v.attributes).map(k => k.toLowerCase())))]
    : [];

  return (
    <div className="container">
      <div style={{ marginBottom: 16, fontSize: 14 }}>
        <Link to="/shop">&larr; Back to Shop</Link>
      </div>
      <div className="product-detail">
        <div>
          <div style={{ position: 'relative' }}>
            {displayImg ? (
              <img src={displayImg} alt={product.name} className="product-detail-img" />
            ) : (
              <div className="product-detail-placeholder">&#128230;</div>
            )}
            {/* VIP badge on image */}
            {product.vip_only && (
              <div style={{ position:'absolute', top:12, left:12, background:'linear-gradient(135deg,#4f46e5,#7c3aed)', color:'white', fontSize:13, fontWeight:800, padding:'5px 14px', borderRadius:100, zIndex:2 }}>
                💎 VIP Exclusive
              </div>
            )}
          </div>
          {allImages.length > 1 && (
            <div className="image-thumbnails">
              {allImages.map((img, i) => (
                <img key={i} src={getImageUrl(img)} alt={`${product.name} ${i + 1}`}
                  className={`image-thumbnail ${selectedImg === i ? 'active' : ''}`}
                  onClick={() => setSelectedImg(i)} />
              ))}
            </div>
          )}
        </div>
        <div>
          {/* Admin edit toggle */}
          {isAdmin && (
            <div style={{ marginBottom: 16 }}>
              {editing ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={saving}>{saving ? 'Saving...' : '\u2713 Save Changes'}</button>
                  <button className="btn btn-outline btn-sm" onClick={() => setEditing(false)}>Cancel</button>
                  <a href={`/admin/products?edit=${id}`} className="btn btn-outline btn-sm" style={{ fontSize: 12 }}>Full Edit</a>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={openEdit} style={{ fontSize: 12 }}>
                    &#9998; Edit Product
                  </button>
                  <a href={`/admin/products?edit=${id}`} className="btn btn-outline btn-sm" style={{ fontSize: 12 }}>
                    &#128221; Edit in Admin
                  </a>
                  <a href={`/admin/products?variants=${id}`} className="btn btn-outline btn-sm" style={{ fontSize: 12 }}>
                    &#127775; Manage Variants
                  </a>
                </div>
              )}
            </div>
          )}

          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Product Name</label>
                <input style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #4f46e5', borderRadius: 8, fontSize: 15, fontWeight: 700, boxSizing: 'border-box' }}
                  value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Price ($)</label>
                  <input type="number" step="0.01" style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                    value={editForm.price} onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Stock</label>
                  <input type="number" style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                    value={editForm.stock_quantity} onChange={e => setEditForm(f => ({ ...f, stock_quantity: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Status</label>
                  <select style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: 'white' }}
                    value={editForm.is_active} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.value }))}>
                    <option value={1}>Active</option>
                    <option value={0}>Hidden</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Brand</label>
                <input style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  value={editForm.brand} onChange={e => setEditForm(f => ({ ...f, brand: e.target.value }))} />
              </div>
              {categories.length > 0 && (
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Category</label>
                  <select style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: 'white' }}
                    value={editForm.category_id} onChange={e => setEditForm(f => ({ ...f, category_id: e.target.value }))}>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Description</label>
                <textarea style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', minHeight: 80, resize: 'vertical' }}
                  value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
              </div>
            </div>
          ) : (
            <>
          {product.brand && <div style={{ color: '#007185', fontSize: 14, marginBottom: 4 }}>{product.brand}</div>}
          <h1>{product.name}</h1>
          {product.category_name && (
            <div style={{ fontSize: 13, color: '#565959', marginTop: 4 }}>
              in <Link to={`/shop?category=${product.category_slug}`}>{product.category_name}</Link>
            </div>
          )}
            </>
          )}
          {reviewStats && reviewStats.count > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: 4 }}>
              <Stars rating={reviewStats.avg} size={18} />
              <span style={{ fontWeight: 700, fontSize: 16 }}>{Number(reviewStats.avg).toFixed(1)}</span>
              <span style={{ color: '#6b7280', fontSize: 14 }}>({reviewStats.count} review{reviewStats.count !== 1 ? 's' : ''})</span>
            </div>
          )}
          <div style={{ borderBottom: '1px solid #ddd', paddingBottom: 16, marginBottom: 16 }}>
            <div className="price">
              ${effectivePrice.toFixed(2)}
              {selectedVariant?.price_adjustment > 0 && <span style={{ fontSize: 14, color: '#6b7280', marginLeft: 8 }}>(+${selectedVariant.price_adjustment.toFixed(2)})</span>}
              {selectedVariant?.price_adjustment < 0 && <span style={{ fontSize: 14, color: 'var(--success)', marginLeft: 8 }}>(${selectedVariant.price_adjustment.toFixed(2)})</span>}
            </div>
          </div>
          {!editing && product.description && <p className="description">{product.description}</p>}

          {/* Variant Selector */}
          {product.variants && product.variants.length > 0 && (() => {
            // Collect all attribute keys
            const attrKeys = [...new Set(product.variants.flatMap(v => Object.keys(v.attributes)))];
            return (
              <div style={{ marginBottom: 20 }}>
                {attrKeys.map(key => {
                  const values = [...new Set(product.variants.map(v => v.attributes[key]).filter(Boolean))];
                  return (
                    <div key={key} style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontWeight: 700, marginBottom: 8, fontSize: 14, textTransform: 'capitalize' }}>
                        {key}: {selectedVariant?.attributes[key] && <span style={{ fontWeight: 400, color: '#374151' }}>{selectedVariant.attributes[key]}</span>}
                      </label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {values.map(val => {
                          const matchingVariant = product.variants.find(v => v.attributes[key] === val);
                          const isSelected = selectedVariant?.attributes[key] === val;
                          const isOOS = matchingVariant && matchingVariant.stock_quantity === 0;
                          const isColor = key.toLowerCase() === 'color';
                          return (
                            <button key={val}
                              onClick={() => setSelectedVariant(matchingVariant || null)}
                              disabled={isOOS}
                              style={{
                                padding: isColor ? '0' : '8px 16px',
                                borderRadius: isColor ? '50%' : 8,
                                width: isColor ? 36 : 'auto',
                                height: isColor ? 36 : 'auto',
                                border: isSelected ? '2px solid var(--primary)' : '1.5px solid #d1d5db',
                                background: isColor ? val : (isSelected ? 'var(--primary)' : 'white'),
                                color: isSelected && !isColor ? 'white' : '#374151',
                                fontSize: 13, fontWeight: 600,
                                cursor: isOOS ? 'not-allowed' : 'pointer',
                                opacity: isOOS ? 0.4 : 1,
                                outline: isSelected ? '2px solid var(--primary)' : 'none',
                                outlineOffset: 2,
                                boxShadow: isSelected ? '0 0 0 3px rgba(201,162,39,0.15)' : 'none',
                              }}
                              title={isOOS ? 'Out of stock' : val}
                            >
                              {!isColor && val}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {!selectedVariant && (
                  <p style={{ fontSize: 13, color: '#ef4444', marginTop: 4 }}>Please select options above</p>
                )}
              </div>
            );
          })()}
          {(product.gender || product.style || product.material || product.color || product.dimensions) && (
            <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: 14, color: '#333' }}>
              {product.gender && <div><strong>Gender:</strong> {product.gender}</div>}
              {product.style && <div><strong>Style:</strong> {product.style}</div>}
              {!variantAttrKeys.includes('material') && product.material && <div><strong>Material:</strong> {product.material}</div>}
              {!variantAttrKeys.includes('color') && product.color && <div><strong>Color:</strong> {product.color}</div>}
              {!variantAttrKeys.includes('size') && product.dimensions && <div><strong>Size:</strong> {product.dimensions}</div>}
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <span className={inStock ? 'in-stock' : 'out-of-stock'} style={{ fontWeight: 600 }}>
              {inStock ? `In Stock (${effectiveStock} available)` : 'Out of Stock'}
            </span>
            {viewerCount > 0 && (
              <span style={{ marginLeft: 14, fontSize: 13, color: '#dc2626', fontWeight: 500 }}>
                &#128065; {viewerCount} people viewing this right now
              </span>
            )}
          </div>          {inStock && (
            <>
              <div className="qty-control">
                <button onClick={() => setQty(q => Math.max(1, q - 1))}>-</button>
                <span>{qty}</span>
                <button onClick={() => setQty(q => Math.min(product.stock_quantity, q + 1))}>+</button>
              </div>
              {product.vip_only && !isVIP && !isAdmin ? (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <button disabled style={{ flex:1, padding:'14px', background:'#e5e7eb', color:'#9ca3af', border:'none', borderRadius:8, fontWeight:700, fontSize:15, cursor:'not-allowed' }}>
                      🔒 Add to Cart
                    </button>
                    <button disabled style={{ flex:1, padding:'14px', background:'#e5e7eb', color:'#9ca3af', border:'none', borderRadius:8, fontWeight:700, fontSize:15, cursor:'not-allowed' }}>
                      🔒 Buy Now
                    </button>
                  </div>
                  <a href="/membership" style={{ display:'block', background:'linear-gradient(135deg,#4f46e5,#7c3aed)', color:'white', padding:'13px', borderRadius:8, fontWeight:700, fontSize:14, textDecoration:'none', textAlign:'center' }}>
                    💎 Join VIP to Purchase — From $9.99/mo →
                  </a>
                </div>
              ) : (
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                <button className="btn btn-primary btn-lg" onClick={handleAdd} disabled={added}
                  style={{ opacity: added ? 0.8 : 1, cursor: added ? 'default' : 'pointer' }}>
                  {added ? '✓ Added to Cart!' : 'Add to Cart'}
                </button>
                <button className="btn btn-accent btn-lg" onClick={() => { if (!added) { handleAdd(); window.location.href = '/cart'; } }}>
                  Buy Now
                </button>
                {user && (
                  <button onClick={() => toggle(parseInt(id))}
                    style={{ padding: '0 18px', border: `2px solid ${saved ? '#ef4444' : '#ddd'}`, borderRadius: 100, background: saved ? '#fef2f2' : 'white', color: saved ? '#ef4444' : '#6b7280', fontSize: 20, cursor: 'pointer', transition: 'all 0.15s' }}
                    title={saved ? 'Remove from wishlist' : 'Save to wishlist'}>
                    {saved ? '\u2665' : '\u2661'}
                  </button>
                )}
              </div>
              )}
            </>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <div style={{ marginTop: 48 }}>
          <h2 className="section-title">You May Also Like</h2>
          <div className="products-grid">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}

      {/* Reviews Section */}
      <div className="reviews-section">
        <h2 className="section-title">Customer Reviews</h2>

        {reviewStats && reviewStats.count > 0 ? (
          <div className="reviews-summary">
            <div className="reviews-avg">
              <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1 }}>{Number(reviewStats.avg).toFixed(1)}</div>
              <Stars rating={reviewStats.avg} size={24} />
              <div style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>{reviewStats.count} review{reviewStats.count !== 1 ? 's' : ''}</div>
            </div>
            <div className="reviews-bars">
              {[5,4,3,2,1].map(n => (
                <div key={n} className="review-bar-row">
                  <span style={{ fontSize: 13, width: 32 }}>{n}{STAR}</span>
                  <div className="review-bar-bg">
                    <div className="review-bar-fill" style={{ width: reviewStats.count ? `${(reviewStats[`r${n}`]/reviewStats.count)*100}%` : '0%' }} />
                  </div>
                  <span style={{ fontSize: 13, color: '#6b7280', width: 24 }}>{reviewStats[`r${n}`]}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p style={{ color: '#6b7280', marginBottom: 24 }}>No reviews yet. Be the first to review this product!</p>
        )}

        <div className="reviews-list">
          {reviews.map(r => (
            <div key={r.id} className="review-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <Stars rating={r.rating} size={16} />
                  {r.title && <div style={{ fontWeight: 700, fontSize: 15, marginTop: 4 }}>{r.title}</div>}
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>{new Date(r.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
              </div>
              {r.body && <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0 }}>{r.body}</p>}
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>&mdash; {r.reviewer_name}</div>
            </div>
          ))}
        </div>

        {!user ? (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginTop: 24, textAlign: 'center' }}>
            <Link to="/login" className="btn btn-primary">Sign in to write a review</Link>
          </div>
        ) : reviewSuccess ? (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 20, marginTop: 24, color: '#166534', fontWeight: 600 }}>
            Thank you for your review!
          </div>
        ) : alreadyReviewed ? (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginTop: 24, color: '#6b7280' }}>
            You have already reviewed this product.
          </div>
        ) : canReview ? (
          <div className="review-form-wrap">
            <h3 style={{ marginBottom: 16, fontSize: 18 }}>Write a Review</h3>
            <form onSubmit={handleReviewSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Your Rating *</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button"
                      onClick={() => setReviewForm(f => ({ ...f, rating: n }))}
                      style={{ fontSize: 32, background: 'none', border: 'none', cursor: 'pointer', color: n <= reviewForm.rating ? '#f59e0b' : '#d1d5db', padding: '0 2px', lineHeight: 1 }}>
                      {n <= reviewForm.rating ? STAR : STAR_EMPTY}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Title (optional)</label>
                <input style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  placeholder="Summarize your experience"
                  value={reviewForm.title}
                  onChange={e => setReviewForm(f => ({ ...f, title: e.target.value }))}
                  maxLength={100}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Review (optional)</label>
                <textarea style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', resize: 'vertical', minHeight: 100 }}
                  placeholder="What did you like or dislike? How is the quality?"
                  value={reviewForm.body}
                  onChange={e => setReviewForm(f => ({ ...f, body: e.target.value }))}
                  maxLength={1000}
                />
              </div>
              {reviewError && <p style={{ color: '#ef4444', fontSize: 14, marginBottom: 12 }}>{reviewError}</p>}
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          </div>
        ) : (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 20, marginTop: 24, color: '#92400e', fontSize: 14 }}>
            Only customers who have purchased this product can write a review.
          </div>
        )}
      </div>
    </div>
  );
}
